import React, { useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Phone, MessageCircle, Clock, Truck, MapPin, Sparkles } from "lucide-react";
import "./flyer.css";
import { HERO_IMAGE as HERO_IMG } from "../lib/siteImages";

const PHONE_DISPLAY = "06 23 06 03 08";
const PHONE_INTL = "+33623060308";
const WHATSAPP_URL = "https://wa.me/33623060308";
const LANDING_URL = "https://noctua-riviera.com/";

const Flyer = () => {
  useEffect(() => {
    document.title = "Flyer — Noctua Riviera";
    // Allow ?side=recto | ?side=verso to pre-set the print mode (used by headless export)
    const params = new URLSearchParams(window.location.search);
    const side = params.get("side");
    if (side === "recto" || side === "verso") {
      document.body.setAttribute("data-print-side", side);
    }
    const cleanup = () => document.body.removeAttribute("data-print-side");
    window.addEventListener("afterprint", cleanup);
    return () => window.removeEventListener("afterprint", cleanup);
  }, []);

  const printSide = (side) => {
    document.body.setAttribute("data-print-side", side);
    // Give the browser a tick to apply the attribute before opening print dialog
    setTimeout(() => window.print(), 50);
  };

  return (
    <div className="flyer-wrap">
      {/* Toolbar (screen only) */}
      <div className="flyer-toolbar no-print" data-testid="flyer-toolbar">
        <div className="flyer-toolbar-inner">
          <span className="flyer-toolbar-brand">NOCTUA <em>Riviera</em> · Flyer A5 recto/verso</span>
          <div className="flyer-toolbar-actions">
            <span className="flyer-toolbar-hint">A5 · Marges: aucune · Arrière-plans activés</span>
            <div className="flyer-toolbar-btns">
              <button
                onClick={() => printSide("recto")}
                className="flyer-print-btn flyer-print-btn-outline"
                data-testid="flyer-print-recto-btn"
              >
                PDF Recto
              </button>
              <button
                onClick={() => printSide("verso")}
                className="flyer-print-btn flyer-print-btn-outline"
                data-testid="flyer-print-verso-btn"
              >
                PDF Verso
              </button>
              <button
                onClick={() => printSide("both")}
                className="flyer-print-btn"
                data-testid="flyer-print-btn"
              >
                PDF Recto + Verso
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ============ RECTO ============ */}
      <section className="flyer-page flyer-recto" data-testid="flyer-recto">
        <img src={HERO_IMG} alt="" className="flyer-bg" />
        <div className="flyer-bg-shade" />
        <div className="flyer-grain" aria-hidden="true" />

        <div className="flyer-recto-content">
          <header className="flyer-recto-header">
            <div className="flyer-brand">
              <span className="flyer-brand-mark">✦</span>
              <span>NOCTUA <em>Riviera</em></span>
            </div>
            <span className="flyer-eyebrow">Nuit · 24 / 24 · Riviera</span>
          </header>

          <div className="flyer-recto-hero">
            <p className="flyer-kicker">Livraison de nuit · Night delivery</p>
            <h1 className="flyer-title">
              La nuit vous<br />
              <em>appartient.</em>
            </h1>
            <div className="flyer-gold-line" />
            <p className="flyer-lead">
              Pizzas, plateaux, snacks & boissons livrés chez vous<br />
              en 30 min, de Cannes à Monaco — 7 j / 7.
            </p>
            <p className="flyer-lead flyer-lead-en">
              Pizzas, gourmet platters, snacks & drinks delivered to your door<br />
              in 30 min, from Cannes to Monaco — 7 days a week.
            </p>
          </div>

          <footer className="flyer-recto-footer">
            <div className="flyer-cta-block">
              <p className="flyer-cta-label">Appelez / Call</p>
              <p className="flyer-cta-phone">{PHONE_DISPLAY}</p>
              <p className="flyer-cta-hint">FR · EN — Réponse immédiate</p>
            </div>
            <div className="flyer-qr-block">
              <div className="flyer-qr-frame">
                <QRCodeSVG
                  value={LANDING_URL}
                  size={140}
                  bgColor="#f8f2df"
                  fgColor="#0a0f1a"
                  level="H"
                  includeMargin={false}
                />
              </div>
              <p className="flyer-qr-caption">Scannez le menu<br /><span>Scan the menu</span></p>
            </div>
          </footer>
        </div>
      </section>

      {/* ============ VERSO ============ */}
      <section className="flyer-page flyer-verso" data-testid="flyer-verso">
        <div className="flyer-grain" aria-hidden="true" />

        <header className="flyer-verso-header">
          <div className="flyer-brand flyer-brand-small">
            <span className="flyer-brand-mark">✦</span>
            <span>NOCTUA <em>Riviera</em></span>
          </div>
          <span className="flyer-eyebrow">La carte · The menu</span>
        </header>

        <div className="flyer-verso-grid">
          <article className="flyer-cat">
            <h3>Pizzas · Restauration</h3>
            <p data-testid="flyer-restaurant-hours">Margherita, Regina, Burrata, 4 Fromages, pâtes et desserts. De 11h à 4h.</p>
          </article>
          <article className="flyer-cat">
            <h3>Plateaux · Platters</h3>
            <p>Charcuterie fine & fromages affinés, 2 à 4 personnes. Yacht ready.</p>
          </article>
          <article className="flyer-cat">
            <h3>Boissons · Drinks</h3>
            <p>Sodas, jus, eaux Perrier & Evian, Red Bull, Schweppes — bien frais.</p>
          </article>
          <article className="flyer-cat">
            <h3>Snacks</h3>
            <p>Pringles, Doritos, Haribo, Oreo, Kinder — les indispensables de la nuit.</p>
          </article>
          <article className="flyer-cat">
            <h3>Alcool · Spirits</h3>
            <p>Bières, vins, champagne, vodka & whisky — sur commande, 18+.</p>
          </article>
          <article className="flyer-cat">
            <h3>Tabac · Tobacco</h3>
            <p>Marlboro, Winston, Camel, Philip Morris — 18+.</p>
          </article>
        </div>

        <div className="flyer-info-row">
          <div className="flyer-info">
            <Clock size={14} strokeWidth={1.5} />
            <span><strong>24 / 24</strong> · 7 j / 7</span>
          </div>
          <div className="flyer-info">
            <Truck size={14} strokeWidth={1.5} />
            <span>Livraison <strong>~30 min</strong></span>
          </div>
          <div className="flyer-info">
            <MapPin size={14} strokeWidth={1.5} />
            <span>Cannes → Monaco</span>
          </div>
          <div className="flyer-info">
            <Sparkles size={14} strokeWidth={1.5} />
            <span>Fr · En</span>
          </div>
        </div>

        <div className="flyer-verso-zones">
          Cannes · Antibes · Juan-les-Pins · Cagnes-sur-Mer · Nice ·
          Villefranche-sur-Mer · Beaulieu-sur-Mer · Saint-Jean-Cap-Ferrat ·
          Èze · Cap-d'Ail · Monaco
        </div>

        <footer className="flyer-verso-footer">
          <div className="flyer-verso-contact">
            <div className="flyer-contact-row">
              <Phone size={14} strokeWidth={1.5} />
              <span>{PHONE_DISPLAY}</span>
            </div>
            <div className="flyer-contact-row">
              <MessageCircle size={14} strokeWidth={1.5} />
              <span>WhatsApp · {PHONE_DISPLAY}</span>
            </div>
            <p className="flyer-contact-web">noctua-riviera.com</p>
            <p className="flyer-legal">
              L'abus d'alcool est dangereux pour la santé. Vente interdite aux mineurs.<br />
              Alcohol abuse is dangerous for your health. Sales forbidden to minors.
            </p>
          </div>
          <div className="flyer-verso-qrs">
            <div className="flyer-verso-qr">
              <div className="flyer-qr-frame flyer-qr-frame-sm">
                <QRCodeSVG
                  value={WHATSAPP_URL}
                  size={96}
                  bgColor="#f8f2df"
                  fgColor="#0a0f1a"
                  level="H"
                  includeMargin={false}
                />
              </div>
              <p className="flyer-qr-caption-sm">WhatsApp</p>
            </div>
            <div className="flyer-verso-qr">
              <div className="flyer-qr-frame flyer-qr-frame-sm">
                <QRCodeSVG
                  value={`tel:${PHONE_INTL}`}
                  size={96}
                  bgColor="#f8f2df"
                  fgColor="#0a0f1a"
                  level="H"
                  includeMargin={false}
                />
              </div>
              <p className="flyer-qr-caption-sm">Appeler · Call</p>
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default Flyer;
